import { useTimeZone } from '@/auth/useTimeZone';
import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { buttonVariants } from '@/components/ui/button';
import { EmptyArray, getMyStudentHandoutFeedbacks } from '@/lib/api-calls';
import { pageSizeSchemaWithMax, paginationRequestSchema, usePagination } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { cn } from '@/lib/tailwind-utils';
import { formatPlainDate, isStudent } from '@airreading/common';
import { LARGE_MAX_PAGE_SIZE, StudentHandoutFeedbackRow } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

const HANDOUTS_AND_FEEDBACK = 'handouts-and-feedback';

export const Route = createFileRoute('/_layout/handouts-and-feedback/')({
  beforeLoad: validateRouteRole(isStudent),
  component: HandoutsAndFeedbackPage,
  validateSearch: paginationRequestSchema.extend({
    pageSize: pageSizeSchemaWithMax(LARGE_MAX_PAGE_SIZE, 30),
  }),
});

const columnHelper = createColumnHelper<StudentHandoutFeedbackRow>();

function HandoutsAndFeedbackPage() {
  const { timeZone } = useTimeZone();
  const { pageIndex, pageSize, onPaginationChange } = usePagination(Route.id);

  const { data, error, refetch, isPending } = useQuery(
    getMyStudentHandoutFeedbacks({
      pageIndex,
      pageSize,
    }),
  );

  const rows = useMemo(() => data?.rows ?? EmptyArray, [data]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('sessionStartInstant', {
        id: 'date',
        header: 'Date',
        enableSorting: false,
        cell: ({ getValue }) => {
          const plainDate = getValue().toZonedDateTimeISO(timeZone).toPlainDate();
          return <span>{formatPlainDate(plainDate)}</span>;
        },
      }),
      columnHelper.accessor('teacherDisplayName', {
        id: 'teacher',
        header: 'Teacher',
        enableSorting: false,
        cell: ({ getValue }) => <span>{getValue() || '-'}</span>,
      }),
      columnHelper.accessor('feedbackContent', {
        id: 'feedback',
        header: 'Feedback from teacher',
        enableSorting: false,
        cell: ({ getValue }) => {
          const content = getValue()?.trim();
          return <div className="max-w-md whitespace-pre-wrap break-words">{content || '-'}</div>;
        },
      }),
      columnHelper.accessor('handouts', {
        id: 'handout',
        header: 'Handout',
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const handouts = getValue().filter((url) => url.trim().length > 0);
          if (handouts.length === 0) {
            return <span>-</span>;
          }

          return (
            <div className="flex flex-col gap-2 items-start whitespace-nowrap">
              {handouts.map((href, handoutIndex) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    buttonVariants({ size: 'sm' }),
                    'bg-air-primary text-white hover:bg-air-primary-hover active:bg-air-primary-active hover:text-white',
                  )}
                  data-testid={`handouts-and-feedback-table-link-open-handout-${row.index}-${handoutIndex}`}
                >
                  Open handout
                </a>
              ))}
            </div>
          );
        },
      }),
    ],
    [timeZone],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => `${row.sessionId}__${row.coachId}__${row.studentId}`,
    manualPagination: true,
    pageCount: data ? Math.ceil(data.total / pageSize) : -1,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange,
  });

  return (
    <InfoPage headerText="Handouts & feedback" data-testid={HANDOUTS_AND_FEEDBACK}>
      <EntityTable
        table={table}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        emptyState="No handouts or feedback found"
        emptyStateClassName="translate-y-0"
        totalRowCount={data?.total}
        paginationOptions={[30, 50, 100]}
        rowClassName="odd:bg-white even:bg-[#F7F7F7]"
        data-testid="handouts-and-feedback-table"
        rowTestId={(_, rowIndex) => `handouts-and-feedback-table-row-${rowIndex}`}
      />
    </InfoPage>
  );
}
